/**
 * NewBudgetDialog — crear o editar un presupuesto.
 *
 * Extraído de BudgetsPage (2026-08-26): la ficha del paciente también
 * necesita poder editar un presupuesto sin ir a la pestaña Presupuestos.
 * En modo edición (`budget` presente) el selector de paciente y el
 * "pick" de la agenda del día se ocultan — solo se cambian cirugía,
 * conceptos e importes.
 *
 * ÁCIDO HIALURÓNICO (administración, 21/10/2026; backend/services/hialuronidasa.py).
 * Al CREAR, si los Datos clínicos del paciente dicen que lleva ácido
 * hialurónico, sale arriba un aviso muy visible para el equipo —nunca va al
 * papel: no entra en las notas ni en las líneas— con DÓNDE lo lleva y la
 * pregunta «¿Añadir hialuronidasa?». Sí añade al final la línea
 * «Hialuronidasa (mín 7 días antes de la intervención)» con el importe del
 * Catálogo de cirugías, que se puede cambiar; si el catálogo no lo tiene, la
 * línea entra con el importe vacío y no se crea el presupuesto hasta que se
 * escribe. No la añade dos veces: la línea se reconoce como en todas partes
 * (`esLineaDeHialuronidasa`, el trozo «hialuronid»). No cierra el aviso.
 * Ni una cifra de la clínica aquí: el importe lo da el servidor.
 *
 * Revisión del mismo día: el aviso sale también al EDITAR un presupuesto que
 * no se ha entregado («pendiente» o «draft»), que es como administración
 * verifica los que nacen del evolutivo. Si ya lleva la línea, solo informa.
 * Y la hialuronidasa que el evolutivo dejó sin importe (el Catálogo no lo
 * daba, `hialuronidasa_importe_pendiente`) se abre vacía y frena el guardado
 * igual que al crear: hay que escribir un importe mayor que cero, o quitarla.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, formatEUR, formatDate, fullName } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Plus, Trash2, Loader2, CalendarDays, UserPlus, Check, Sparkles, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { PatientCombobox } from './PatientCombobox';
import { QuickNewPatientDialog } from './QuickNewPatientDialog';
import {
  esLineaDeHialuronidasa, indiceLineaDeCirugia, marcasDelPaciente, textoRecargo,
  textoRecargoDelPresupuesto, useMarcasPaciente,
} from '../lib/marcasPaciente';
import AvisoHialuronico, { importePendiente, useAvisoHialuronico } from './AvisoHialuronico';

// Los presupuestos que todavía no se han entregado: al editarlos, el aviso del
// ácido hialurónico también pregunta (revisión del 21/10/2026). Sin estado es
// un borrador, como lo lee el servidor.
const SIN_ENTREGAR = ['pendiente', 'draft'];

// ----- Helpers extracted to keep NewBudgetDialog focused on its own state -----
const _normSearch = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** La hialuronidasa va SIEMPRE debajo de la cirugía (dirección, 2026-08-25:
 *  «tiene que salir en la línea de abajo»): el resto, en su orden, y ella al
 *  final. */
const hialuronidasaAlFinal = (lineas) => [
  ...lineas.filter((it) => !esLineaDeHialuronidasa(it.concept)),
  ...lineas.filter((it) => esLineaDeHialuronidasa(it.concept)),
];

function AgendaQuickPickPanel({ todayAgenda, todayLoading, selectedPatientId, visibleAppointments, onPickAppointment, onNewPatient }) {
  const [open, setOpen] = useState(false);
  const titleSuffix = todayAgenda?.agenda_date ? ` · ${todayAgenda.agenda_date}` : '';
  const title = todayAgenda?.is_today ? 'Pacientes de la agenda de hoy' : `Última agenda disponible${titleSuffix}`;
  const totalCount = visibleAppointments.length;

  // Find which appointment is currently selected (if any) so we can highlight it.
  const selectedAppointment = visibleAppointments.find((a) => a.patient_id && a.patient_id === selectedPatientId);
  const triggerLabel = selectedAppointment
    ? `${selectedAppointment.start_time} · ${selectedAppointment.patient_name}`
    : totalCount > 0
      ? `Elegir paciente de la agenda (${totalCount} citas)`
      : 'Sin pacientes en la agenda';

  return (
    <div className="rounded-lg border border-[rgba(46,110,94,0.35)] bg-[rgba(46,110,94,0.06)] p-3" data-testid="budget-agenda-quickpick">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#13231E]">
          <CalendarDays className="w-4 h-4" />
          {title}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onNewPatient}
          className="text-[#13231E] hover:bg-white"
          data-testid="budget-new-patient-button"
        >
          <UserPlus className="w-4 h-4 mr-1" /> Nuevo paciente
        </Button>
      </div>
      {todayLoading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando agenda…
        </div>
      ) : totalCount === 0 ? (
        <div className="text-xs text-muted-foreground">No hay citas en la agenda del día.</div>
      ) : (
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                data-testid="budget-agenda-trigger"
                className="w-full justify-between font-normal bg-white"
              >
                <span className={`truncate ${!selectedAppointment ? 'text-muted-foreground' : 'text-[#13231E] font-medium'}`}>
                  {triggerLabel}
                </span>
                <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command filter={(v, s) => (_normSearch(v).includes(_normSearch(s)) ? 1 : 0)}>
                <CommandInput placeholder="Buscar por hora, nombre o tipo de visita…" data-testid="budget-agenda-search" />
                <CommandList>
                  <CommandEmpty>Sin coincidencias.</CommandEmpty>
                  <CommandGroup>
                    {visibleAppointments.map((a) => {
                      const isSelected = !!a.patient_id && a.patient_id === selectedPatientId;
                      const needsCreate = !a.patient_id;
                      const searchString = `${a.start_time} ${a.patient_name} ${a.visit_type || ''}`;
                      return (
                        <CommandItem
                          key={`${a.start_time}-${a.patient_name}`}
                          value={searchString}
                          onSelect={() => { onPickAppointment(a); setOpen(false); }}
                          data-testid="budget-agenda-item"
                        >
                          <span className="font-mono text-[11px] text-muted-foreground w-12 shrink-0">{a.start_time}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{a.patient_name}</div>
                            {a.visit_type && (
                              <div className="text-[11px] text-muted-foreground truncate">{a.visit_type}</div>
                            )}
                          </div>
                          {isSelected
                            ? <Check className="w-4 h-4 ml-2 text-[#13231E] shrink-0" />
                            : needsCreate
                              ? <Sparkles className="w-3.5 h-3.5 ml-2 opacity-60 shrink-0" />
                              : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Ya en BBDD</span>
            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Nuevo (al pulsar lo crearás)</span>
          </div>
        </>
      )}
    </div>
  );
}

// EL MÉDICO DEL PRESUPUESTO (26/10/2026): decide el membrete del PDF (el de
// la clínica o el de la marca de sus documentos). El servidor propone uno
// (la última consulta previa del paciente, o quien lo crea si es médico) y
// dice con qué membrete saldría con cada uno; se puede cambiar, y viaja como
// `surgeon_id`. Uno que ya salió hacia el paciente no se cambia.
const SIN_MEDICO = '__sin_medico__';
const POR_QUE = {
  consulta_previa: 'propuesto: el de su última consulta previa',
  creador: 'propuesto: quien lo crea',
  evolutivo: 'el del evolutivo',
};

// EL FORMATO (administración, 27/10/2026): «el sistema sugiere el formato
// por defecto [según el médico], pero siempre deja elegir». Los formatos son
// las marcas de Configuración → Marcas (las da el servidor; sin ninguna, no
// hay selector) y cada médico trae el que propone. Se preselecciona por el
// médico y se vuelve a proponer al cambiarlo, SALVO que alguien lo haya
// tocado a mano. Viaja como `formato_clave`. Uno que ya salió hacia el
// paciente no se cambia.
const formatoPropuestoPara = (info, medicoId) => {
  const m = medicoId ? (info.medicos || []).find((x) => x.id === medicoId) : null;
  return (m ? m.formato_clave : info.formato_sin_medico) || '';
};

function NewBudgetDialog({ open, onOpenChange, onCreated, initialPatient: initialPatientProp, patients: patientsProp, onPatientsRefresh, budget, pacienteFijo }) {
  // `budget` set → modo edición: se cambia la cirugía y los importes de uno ya creado.
  const editing = !!budget;
  // `pacienteFijo` (la ficha completa) → se crea DESDE LA FICHA del paciente
  // (30/10/2026, administración): el paciente viene puesto y no se cambia, y
  // al guardar el diálogo se cierra sin sacar a nadie de la ficha. Su ficha
  // hace de lista, así las marcas con recargo se leen sin pedirla otra vez.
  const initialPatient = pacienteFijo?.id || initialPatientProp;
  const patients = pacienteFijo ? [pacienteFijo] : patientsProp;
  const [surgeries, setSurgeries] = useState([]);
  const [template_code, setTemplateCode] = useState('');
  const [patient_id, setPatientId] = useState(initialPatient || '');
  // Each item gets a stable local _uid used only for React keys; stripped before POST.
  const newItem = (concept = '', amount = 0) => ({ _uid: `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, concept, amount });
  const [items, setItems] = useState([newItem()]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Today's agenda quick-pick state
  const [todayAgenda, setTodayAgenda] = useState(null); // { agenda_id, agenda_date, is_today, appointments }
  const [todayLoading, setTodayLoading] = useState(false);

  // Quick "new patient" sub-dialog state
  const [quickNewOpen, setQuickNewOpen] = useState(false);
  const [quickNewSeed, setQuickNewSeed] = useState('');

  // MARCAS DE PACIENTE (14/10/2026): si el paciente tiene una marca con
  // recargo, al CREAR el presupuesto se suma al precio de la línea de la
  // cirugía. Quien lo crea ELIGE esa línea (viene puesta la que se detecta, o
  // «Ninguna» con un aviso si no se reconoce ninguna: las cirugías escritas a
  // mano no se reconocen) y el servidor la valida (`recargo_linea`).
  const marcasConfiguradas = useMarcasPaciente({ activo: open });
  const [pacienteElegido, setPacienteElegido] = useState(null);
  // La ficha pedida aparte, si el paciente no viene en la lista (los más
  // recientes): sin ella no se sabría que tiene la marca.
  const [fichaPedida, setFichaPedida] = useState(null);
  // null = la línea detectada; 'ninguna' o el _uid de una línea = la elegida.
  const [lineaRecargo, setLineaRecargo] = useState(null);

  // ÁCIDO HIALURÓNICO (21/10/2026): el aviso del paciente. Al crear, siempre;
  // al editar, solo si el presupuesto no se ha entregado (así se verifican los
  // que nacen del evolutivo). null = nada que avisar; {error: true} = no se ha
  // podido mirar. Contestado (Sí o No), se cierra hasta que cambie el paciente
  // o se vuelva a abrir el diálogo.
  const avisaAlEditar = editing && SIN_ENTREGAR.includes(budget?.status || 'draft');
  const pacienteDelAviso = editing ? (budget?.patient_id || '') : patient_id;
  const avisoAh = useAvisoHialuronico({
    patientId: pacienteDelAviso, activo: open && (!editing || avisaAlEditar),
  });
  const [avisoAhCerrado, setAvisoAhCerrado] = useState(false);
  useEffect(() => { setAvisoAhCerrado(false); }, [open, pacienteDelAviso]);

  // El «Médico»: lo que dice el servidor (null = sin selector) y el elegido
  // (null = aún no se sabe; '' = ninguno, el formato de la clínica).
  const [medicoInfo, setMedicoInfo] = useState(null);
  const [medico, setMedico] = useState(null);
  const [medicoInicial, setMedicoInicial] = useState(null);
  // El «Formato» (27/10/2026): la clave elegida (null = aún no se sabe) y si
  // alguien la ha tocado a mano (entonces cambiar de médico no la mueve).
  const [formato, setFormato] = useState(null);
  const formatoTocado = useRef(false);
  const budgetId = budget?.id || '';
  useEffect(() => {
    setMedicoInfo(null);
    setMedico(null);
    setMedicoInicial(null);
    setFormato(null);
    formatoTocado.current = false;
    if (!open) return undefined;
    const params = editing ? { budget_id: budgetId } : (patient_id ? { patient_id } : null);
    if (!params) return undefined;
    let vivo = true;
    api.get('/api/budgets/medico-propuesto', { params }).then((r) => {
      const d = r?.data;
      if (!vivo || !d || !Array.isArray(d.medicos)) return;
      setMedicoInfo(d);
      setMedico(d.propuesto?.id || '');
      setMedicoInicial(d.propuesto?.id || '');
      // Editando uno que ya tiene formato elegido, se queda el suyo (cuenta
      // como tocado a mano); si no, el que propone su médico.
      const actual = d.formato_actual || '';
      formatoTocado.current = !!actual;
      setFormato(actual || formatoPropuestoPara(d, d.propuesto?.id || ''));
    }).catch(() => {});
    return () => { vivo = false; };
  }, [open, editing, budgetId, patient_id]);
  const medicoElegido = medicoInfo && medico ? medicoInfo.medicos.find((m) => m.id === medico) : null;
  const hayFormatos = Array.isArray(medicoInfo?.formatos) && medicoInfo.formatos.length > 0;
  const formatoElegido = hayFormatos && formato ? medicoInfo.formatos.find((f) => f.clave === formato) : null;
  // Con selector de formato, el membrete es el del formato elegido; sin él,
  // el que el servidor dijo para el médico.
  const membreteQueSaldra = formatoElegido
    ? formatoElegido.nombre
    : (medicoElegido ? medicoElegido.membrete : (medicoInfo?.membrete_sin_medico || ''));
  // Cambiar de médico vuelve a proponer el formato, salvo que se haya tocado.
  const elegirMedico = (v) => {
    setMedico(v);
    if (medicoInfo && !formatoTocado.current) setFormato(formatoPropuestoPara(medicoInfo, v));
  };
  const elegirFormato = (v) => {
    formatoTocado.current = true;
    setFormato(v);
  };
  const porQue = medicoInfo && medico && medico === medicoInfo.propuesto?.id
    ? POR_QUE[medicoInfo.propuesto?.origen] : '';
  // Lo que viaja: el elegido (o null, ninguno), solo si hay selector y se puede
  // cambiar; al editar uno que ya tenía médico elegido, solo si se ha cambiado
  // (un médico dado de baja después no impide corregir el presupuesto).
  const conMedico = () => {
    if (!medicoInfo || !medicoInfo.editable || medico === null) return {};
    if (editing && budget?.medico_elegido && medico === medicoInicial) return {};
    return { surgeon_id: medico || null };
  };
  // El formato viaja siempre que hay selector y se puede cambiar (el servidor
  // solo lo apunta si cambia).
  const conFormato = () => {
    if (!medicoInfo || !medicoInfo.editable || !hayFormatos || formato === null) return {};
    return { formato_clave: formato };
  };

  useEffect(() => {
    if (!open) return;
    api.get('/api/surgeries').then(r => setSurgeries(r.data));
    if (editing) {
      // Precargamos el presupuesto existente; el paciente ya no se cambia.
      setPatientId(budget.patient_id || '');
      setTemplateCode(budget.surgery_code || '');
      // La hialuronidasa que el evolutivo dejó sin importe se abre VACÍA y
      // marcada: no se guarda hasta escribir un importe mayor que cero.
      const pendiente = importePendiente(budget);
      setItems((budget.items || []).length
        ? budget.items.map((it) => (pendiente && esLineaDeHialuronidasa(it.concept) && !(Number(it.amount) > 0)
          ? { ...newItem(it.concept, ''), _importePendiente: true }
          : newItem(it.concept, Number(it.amount || 0))))
        : [newItem()]);
      setNotes(budget.notes || '');
      return;
    }
    setPatientId(initialPatient || '');
    setPacienteElegido(null);
    setLineaRecargo(null);
    setTemplateCode('');
    setItems([newItem()]);
    setNotes('');
    // (2026-08-29: retirado el flujo de agendas PDF legacy; los chips de
    // "agenda de hoy" venían de ahí y llevaban meses saliendo vacíos.)
    setTodayAgenda(null);
    setTodayLoading(false);
  }, [open, initialPatient, editing, budget]);

  const onPickTemplate = (code) => {
    setTemplateCode(code);
    const s = surgeries.find(x => x.code === code);
    if (!s) return;
    setItems(currentItems => {
      // Editando: cambiar de cirugía sustituye la primera línea (la cirugía),
      // conservando las líneas extra que se hubieran añadido a mano. Si el
      // presupuesto ya llevaba el recargo de una marca de paciente (14/10/2026),
      // va dentro del precio de la cirugía y se conserva: ni se pierde al
      // cambiar de cirugía ni se suma otra vez.
      if (editing && currentItems.length) {
        const recargo = (budget?.recargos_aplicados || [])
          .filter((r) => Number(r?.linea ?? 0) === 0)
          .reduce((suma, r) => suma + Number(r?.importe || 0), 0);
        return currentItems.map((it, i) => i === 0
          ? { ...it, concept: s.name, amount: Number(s.price || 0) + recargo }
          : it);
      }
      // La primera fila vacía se rellena; si no hay, se añade una. Y la
      // hialuronidasa, si ya está (la del aviso del ácido hialurónico), se
      // queda debajo de la cirugía.
      const hueco = currentItems.findIndex((it) => !it.concept);
      const nuevas = hueco >= 0
        ? currentItems.map((it, i) => (i === hueco ? newItem(s.name, Number(s.price || 0)) : it))
        : [...currentItems, newItem(s.name, Number(s.price || 0))];
      return hialuronidasaAlFinal(nuevas);
    });
  };

  // «¿Añadir hialuronidasa?» → Sí. Una sola línea: si ya la lleva (escrita a
  // mano o elegida del catálogo de cirugías), no se añade otra. Sin importe en
  // el Catálogo de cirugías entra vacía y marcada, para escribirlo a mano.
  const anadirHialuronidasa = () => {
    const aviso = avisoAh;
    setAvisoAhCerrado(true);
    if (!aviso?.concepto) return;
    if (items.some((it) => esLineaDeHialuronidasa(it.concept))) {
      toast.info('El presupuesto ya lleva la línea de hialuronidasa: no se añade otra.');
      return;
    }
    const conImporte = typeof aviso.importe === 'number';
    setItems((arr) => hialuronidasaAlFinal([
      ...arr,
      { ...newItem(aviso.concepto, conImporte ? aviso.importe : ''), _importePendiente: !conImporte },
    ]));
    if (!conImporte) {
      toast.warning(aviso.aviso_importe || 'Escribe a mano el importe de la hialuronidasa.');
    }
  };

  const onPickAgendaAppointment = (appt) => {
    if (appt.patient_id) {
      // Matched against an existing patient → simply select.
      setPatientId(appt.patient_id);
      toast.success(`${appt.patient_name} seleccionado`);
    } else {
      // Not matched → open the quick-create dialog pre-filled with the name.
      setQuickNewSeed(appt.patient_name || '');
      setQuickNewOpen(true);
    }
  };

  const onQuickPatientCreated = (newPatient) => {
    // Make the new patient instantly selectable & selected.
    onPatientsRefresh?.();
    setPatientId(newPatient.id);
  };

  // La línea que entró sin importe (`_importePendiente`) se queda vacía hasta
  // que se escribe uno, y pendiente hasta que es mayor que cero: un «0» puesto
  // solo sería un precio que nadie ha dicho (y el servidor no lo deja salir).
  const updateItem = (i, k, v) => setItems(arr => arr.map((it, idx) => {
    if (idx !== i) return it;
    if (k !== 'amount') return { ...it, [k]: v };
    if (it._importePendiente && v === '') return { ...it, amount: '' };
    return { ...it, amount: Number(v), _importePendiente: !!it._importePendiente && !(Number(v) > 0) };
  }));
  const addItem = () => setItems(arr => [...arr, newItem()]);
  const removeItem = (i) => setItems(arr => arr.filter((_, idx) => idx !== i));
  const total = useMemo(() => items.reduce((s, it) => s + Number(it.amount || 0), 0), [items]);
  // Las marcas con recargo del paciente elegido (solo al crear).
  const hayRecargos = marcasConfiguradas.some((m) => Number(m.recargo_cirugia) > 0);
  const fichaEnLista = (pacienteElegido?.id === patient_id && pacienteElegido)
    || (patients || []).find((p) => p.id === patient_id) || null;
  // Otro paciente: vuelve a mandar la línea detectada.
  useEffect(() => { setLineaRecargo(null); }, [patient_id]);
  useEffect(() => {
    setFichaPedida(null);
    if (!open || editing || !patient_id || !hayRecargos || fichaEnLista) return undefined;
    let vigente = true;
    Promise.resolve()
      .then(() => api.get(`/api/patients/${patient_id}`))
      .then((r) => { if (vigente && r?.data && !Array.isArray(r.data)) setFichaPedida(r.data); })
      .catch(() => {});
    return () => { vigente = false; };
  }, [open, editing, patient_id, hayRecargos, fichaEnLista]);
  const marcasConRecargo = useMemo(() => {
    if (editing) return [];
    const ficha = fichaEnLista || (fichaPedida?.id === patient_id ? fichaPedida : null);
    return marcasDelPaciente(ficha, marcasConfiguradas).filter((m) => Number(m.recargo_cirugia) > 0);
  }, [editing, fichaEnLista, fichaPedida, patient_id, marcasConfiguradas]);
  // Las líneas que pueden llevar el recargo (con concepto y que no sean la
  // hialuronidasa), la detectada y la que queda puesta.
  const conConcepto = items.filter((it) => it.concept);
  const candidatas = conConcepto.filter((it) => !esLineaDeHialuronidasa(it.concept));
  const iDetectada = indiceLineaDeCirugia(conConcepto, surgeries);
  const uidDetectada = iDetectada >= 0 ? conConcepto[iDetectada]._uid : null;
  const lineaVale = lineaRecargo === 'ninguna' || candidatas.some((it) => it._uid === lineaRecargo);
  const lineaPuesta = (lineaRecargo && lineaVale) ? lineaRecargo : (uidDetectada || 'ninguna');
  const sinReconocer = marcasConRecargo.length > 0 && !uidDetectada && !(lineaRecargo && lineaVale);
  // El aviso discreto del recargo: editando, el que ya lleva el presupuesto;
  // creando, el que se va a sumar a la línea puesta.
  const avisoRecargo = editing
    ? textoRecargoDelPresupuesto(budget)
    : (marcasConRecargo.length && lineaPuesta !== 'ninguna'
      ? textoRecargo(marcasConRecargo.map((m) => m.nombre)) : '');
  const submit = async (e) => {
    e.preventDefault();
    if (!patient_id) return toast.error('Selecciona un paciente');
    const sinImporte = items.find((it) => it.concept && it._importePendiente);
    if (sinImporte) {
      return toast.error(`Escribe el importe de «${sinImporte.concept}» (mayor que 0): no viene del Catálogo de cirugías. Si no se cobra, quita la línea.`);
    }
    // Strip the internal _uid (y la marca de importe pendiente) before sending to backend
    const validItems = items.filter(it => it.concept).map(({ _uid, _importePendiente, ...rest }) => rest);
    if (!validItems.length) return toast.error('Añade al menos un concepto');
    // La línea del recargo, como índice de las líneas que se mandan, SOLO si
    // la persona la ha elegido en el selector (revisión del 14/10/2026). Sin
    // tocarlo no se manda: el servidor la detecta y, si no reconoce ninguna,
    // el rastro dice «no se reconoció» y no «eligieron Ninguna». `null` solo
    // cuando se elige «Ninguna» de verdad.
    const eligioLinea = marcasConRecargo.length > 0 && !!lineaRecargo && lineaVale;
    const recargoLinea = eligioLinea
      ? (lineaRecargo === 'ninguna' ? null : conConcepto.findIndex((it) => it._uid === lineaRecargo))
      : undefined;
    setBusy(true);
    try {
      if (editing) {
        const res = await api.put(`/api/budgets/${budget.id}`, {
          items: validItems,
          surgery_code: template_code || null,
          notes,
          ...conMedico(),
          ...conFormato(),
        });
        toast.success(`Presupuesto ${res.data.number} actualizado · ${formatEUR(res.data.total)}`);
        onCreated && onCreated(res.data);
        onOpenChange(false);
        return;
      }
      const res = await api.post('/api/budgets', {
        patient_id, surgery_code: null, items: validItems, notes, valid_days: 60,
        ...conMedico(),
        ...conFormato(),
        // Solo con una marca con recargo: la línea elegida (o null, «Ninguna»).
        ...(recargoLinea !== undefined ? { recargo_linea: recargoLinea >= 0 ? recargoLinea : null } : {}),
      });
      const auto = res.data.auto_generated || {};
      if (auto.preop_id && auto.instructions_id) {
        toast.success(`Presupuesto ${res.data.number} creado · Preoperatorio + Indicaciones generados automáticamente`);
      } else {
        toast.success(`Presupuesto ${res.data.number} creado`);
      }
      // El recargo de una marca de paciente, si el servidor lo ha sumado.
      const recargoAplicado = textoRecargoDelPresupuesto(res.data);
      if (recargoAplicado) toast.info(`${recargoAplicado} (sumado al precio de la cirugía)`);
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };

  // Show all appointments of the day. The chips container scrolls vertically
  // when there are too many to fit (typical for full-day clinic agendas).
  const visibleAppointments = todayAgenda?.appointments || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Editar presupuesto ${budget.number}` : 'Nuevo presupuesto'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {/* ÁCIDO HIALURÓNICO (21/10/2026): lo primero que se ve al crear, o
              al editar uno sin entregar. Es de pantalla: ni va a las notas ni a
              las líneas, así que no sale en ningún papel ni enlace. Si ya lleva
              la línea, solo informa. */}
          {avisoAh && (avisoAh.error || !avisoAhCerrado) && (
            <AvisoHialuronico
              aviso={avisoAh}
              modo={items.some((it) => esLineaDeHialuronidasa(it.concept)) ? 'info' : 'pregunta'}
              onSi={anadirHialuronidasa}
              onNo={() => setAvisoAhCerrado(true)}
              onCerrar={() => setAvisoAhCerrado(true)}
            />
          )}
          {/* Today's agenda quick-pick — extracted to a dedicated component to
              keep this dialog under control. */}
          {!editing && !pacienteFijo && (todayLoading || (todayAgenda && todayAgenda.appointments?.length > 0)) && (
            <AgendaQuickPickPanel
              todayAgenda={todayAgenda}
              todayLoading={todayLoading}
              selectedPatientId={patient_id}
              visibleAppointments={visibleAppointments}
              onPickAppointment={onPickAgendaAppointment}
              onNewPatient={() => { setQuickNewSeed(''); setQuickNewOpen(true); }}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Paciente*</Label>
              {editing ? (
                <div className="h-10 flex items-center px-3 rounded-md border border-border bg-muted text-sm font-medium">
                  {budget.patient_name}
                </div>
              ) : pacienteFijo ? (
                <div className="h-10 flex items-center px-3 rounded-md border border-border bg-muted text-sm font-medium" data-testid="budget-patient-fijo">
                  {fullName(pacienteFijo) || pacienteFijo.name || ''}
                </div>
              ) : (
                <PatientCombobox value={patient_id} onChange={(id, ficha) => { setPatientId(id); setPacienteElegido(ficha || null); }} patients={patients} dataTestid="budget-patient-select" />
              )}
            </div>
            <div>
              <Label>{editing ? 'Cirugía / Procedimiento' : 'Plantilla / Procedimiento (opcional)'}</Label>
              <Select value={template_code} onValueChange={onPickTemplate}>
                <SelectTrigger data-testid="budget-template-select"><SelectValue placeholder="— elige un procedimiento habitual —" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {surgeries.map(s => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.name}{s.price > 0 ? ` · ${formatEUR(s.price)}` : ' · (precio a definir)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {medicoInfo && (
            <div className="space-y-1" data-testid="budget-medico">
              <Label>Médico</Label>
              <Select value={medico || SIN_MEDICO} onValueChange={(v) => elegirMedico(v === SIN_MEDICO ? '' : v)}
                disabled={!medicoInfo.editable}>
                <SelectTrigger data-testid="budget-medico-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value={SIN_MEDICO}>Sin médico</SelectItem>
                  {medicoInfo.medicos.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}{m.titular ? ' (titular)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="budget-medico-membrete">
                {medicoInfo.editable
                  ? `Saldrá con el membrete de ${membreteQueSaldra}${porQue ? ` (${porQue})` : ''}.`
                  : 'Ya ha salido hacia el paciente: sale con el membrete con el que se envió y su médico no se cambia.'}
              </p>
            </div>
          )}
          {/* EL FORMATO (27/10/2026): propuesto por el médico, se cambia a mano. */}
          {medicoInfo && hayFormatos && (
            <div className="space-y-1" data-testid="budget-formato">
              <Label>Formato</Label>
              <Select value={formato || ''} onValueChange={elegirFormato} disabled={!medicoInfo.editable}>
                <SelectTrigger data-testid="budget-formato-select"><SelectValue placeholder="— elige el formato —" /></SelectTrigger>
                <SelectContent>
                  {medicoInfo.formatos.map((f) => (
                    <SelectItem key={f.clave} value={f.clave}>{f.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="budget-formato-pista">
                {!medicoInfo.editable
                  ? 'Ya ha salido hacia el paciente: sale con el formato con el que se envió.'
                  : (formatoTocado.current
                    ? 'Elegido a mano.'
                    : 'Propuesto por el médico; puedes cambiarlo antes de generar.')}
              </p>
            </div>
          )}
          <Label>Conceptos e importes</Label>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it._uid} className="grid grid-cols-[1fr_140px_36px] gap-2">
                <Input value={it.concept} onChange={(e) => updateItem(i, 'concept', e.target.value)} placeholder="Concepto" />
                <Input
                  type="number" step="0.01" value={it.amount}
                  onChange={(e) => updateItem(i, 'amount', e.target.value)}
                  placeholder={it._importePendiente ? 'Escribe el importe' : 'Importe €'}
                  className={it._importePendiente ? 'border-2 border-red-600' : undefined}
                  data-testid={`budget-item-amount-${i}`}
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Añadir concepto</Button>
          </div>
          {/* MARCAS DE PACIENTE (14/10/2026): a qué línea se suma el recargo.
              Viene puesta la detectada; si no se reconoce ninguna, «Ninguna»
              y el aviso, para que nadie lo pierda sin enterarse. */}
          {!editing && marcasConRecargo.length > 0 && (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 space-y-1.5" data-testid="budget-recargo-linea">
              <Label>Línea a la que se suma el recargo de {marcasConRecargo.map((m) => m.nombre).join(' y ')}</Label>
              <Select value={lineaPuesta} onValueChange={setLineaRecargo}>
                <SelectTrigger data-testid="budget-recargo-linea-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {candidatas.map((it) => (
                    <SelectItem key={it._uid} value={it._uid}>
                      {`${conConcepto.indexOf(it) + 1}. ${it.concept}`}
                    </SelectItem>
                  ))}
                  <SelectItem value="ninguna">Ninguna (este presupuesto no es de cirugía)</SelectItem>
                </SelectContent>
              </Select>
              {sinReconocer && (
                <div className="text-xs font-medium text-amber-800" data-testid="budget-recargo-no-reconocida">
                  No se ha reconocido la línea de la cirugía: elige a cuál se suma el recargo
                </div>
              )}
            </div>
          )}
          <div><Label>Notas (opcional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas adicionales…" /></div>
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {editing ? `Vence: ${formatDate(budget.valid_until)}` : 'Validez: 60 días'}
            </div>
            <div className="text-right">
              <div className="text-xl font-display font-semibold text-[#13231E]">Total: {formatEUR(total)}</div>
              {avisoRecargo && (
                <div className="text-[11px] text-muted-foreground" data-testid="budget-aviso-recargo"
                  title={editing ? 'Ya va dentro del precio de la cirugía' : 'Se suma al precio de la cirugía al crear el presupuesto; el paciente no lo ve aparte'}>
                  {avisoRecargo}{editing ? '' : ' (se suma a la línea elegida al crearlo)'}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={editing ? 'budget-save-button' : 'budget-create-button'}>
              {busy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editing ? 'Guardando…' : 'Creando…'}</>
                : (editing ? 'Guardar cambios' : 'Crear presupuesto')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <QuickNewPatientDialog
        open={quickNewOpen}
        onOpenChange={setQuickNewOpen}
        initialName={quickNewSeed}
        onCreated={onQuickPatientCreated}
      />
    </Dialog>
  );
}


export default NewBudgetDialog;
